<script lang="ts">
  import { createTagsInput, melt, type Tag, type AddTag, type Blur } from "@melt-ui/svelte";
  import { PlusCircle, CrossCircle } from "@poolsideai/web-components/icons";
  import { scale } from "svelte/transition";
  import { Cross } from "../icons/index.js";
  import type { Writable } from "svelte/store";

  export type TagsInputTag = Tag & { active?: boolean };

  const ADD_ALL_OPTION = "Add All";
  const DESELECT_ALL_OPTION = "Deselect All";

  interface Props {
    tags: Writable<TagsInputTag[]>;
    unique?: boolean;
    addOnPaste?: boolean;
    addTag?: AddTag | undefined;
    defaultTags?: string[] | undefined;
    placeholder: string;
    maxTags?: any;
    blur?: Blur;
    class?: string;
    dropdownClass?: string;
    suggestions?: string[];
    maxSuggestions?: number;
    minQueryLength?: number;
    highlightMatches?: boolean;
    disabled?: boolean;
    /** When false, chips stay on a single line with horizontal scroll instead of wrapping. */
    wrap?: boolean;
  }

  let {
    tags,
    unique = true,
    addOnPaste = true,
    addTag = undefined,
    defaultTags = undefined,
    placeholder,
    maxTags = Infinity,
    blur = "nothing",
    class: className = "",
    suggestions = [],
    maxSuggestions = 100,
    minQueryLength = 1,
    disabled,
    dropdownClass = "",
    wrap = true,
  }: Props = $props();

  let inputValue = $state("");
  let inputElement: HTMLInputElement;
  let highlightedIndex = $state(-1);
  let dropdownScrollContainer = $state<HTMLElement>();
  let isFocused = $state(false);
  let containerElement: HTMLDivElement;
  let dropdownPosition = $state({ top: 0, left: 0, width: 0 });

  // melt setup
  const {
    elements: { root, input, tag, deleteTrigger, edit },
    states: { tags: tagsState },
    // svelte-ignore state_referenced_locally
  } = createTagsInput({
    defaultTags,
    unique,
    add: addTag || ((value: string) => value),
    addOnPaste,
    maxTags,
    tags: tags as Writable<Tag[]>,
    trim: true,
    blur,
  });

  const suggestionState = $derived(() => {
    if (!suggestions.length) {
      return { filtered: [], available: 0, show: false };
    }

    // only show dropdown when input is focused
    if (!isFocused) {
      return { filtered: [], available: 0, show: false };
    }

    const tagsLength = $tagsState?.length ?? 0;
    const atMaxTags = tagsLength >= maxTags;
    const hasExistingTags = tagsLength > 0;

    if (atMaxTags) {
      const withDeselectAll = hasExistingTags ? [DESELECT_ALL_OPTION] : [];
      return {
        filtered: withDeselectAll,
        available: 0,
        show: withDeselectAll.length > 0,
      };
    }

    const existingValues = new Set(
      ($tagsState ?? []).map((tag) => tag?.value?.toLowerCase()).filter(Boolean),
    );
    const filtered = suggestions
      .filter((s) => {
        const lower = s.toLowerCase();
        const hasMinInput = inputValue && inputValue.length >= minQueryLength;
        const matchesInput = !hasMinInput || lower.includes(inputValue.toLowerCase());
        return matchesInput && !existingValues.has(lower);
      })
      .slice(0, maxSuggestions);

    // Add "Deselect All" option if there are existing tags
    const withDeselectAll = hasExistingTags ? [DESELECT_ALL_OPTION] : [];
    // Only show "Add All" if we can add more than one item
    const canAddMultiple = filtered.length > 1 && tagsLength + filtered.length <= maxTags;
    const withAddAll =
      filtered.length > 0 && canAddMultiple ? [ADD_ALL_OPTION, ...filtered] : filtered;
    const allOptions = [...withDeselectAll, ...withAddAll];

    return {
      filtered: allOptions,
      available: filtered.length,
      show: allOptions.length > 0,
    };
  });

  $effect(() => {
    suggestionState;
    highlightedIndex = -1;
  });

  // portal to avoid clipping out
  $effect(() => {
    if (suggestionState().show && containerElement && typeof window !== "undefined") {
      const rect = containerElement.getBoundingClientRect();
      dropdownPosition = {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      };
    }
  });

  // keyboard navigation
  $effect(() => {
    if (!inputElement || !suggestions.length) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const { filtered, show } = suggestionState();
      if (!show) return;

      const maxIndex = filtered.length - 1;

      switch (e.key) {
        case "ArrowDown":
        case "Tab":
          e.preventDefault();
          highlightedIndex = Math.min(highlightedIndex + 1, maxIndex);
          scrollToHighlighted();
          break;

        case "ArrowUp":
          e.preventDefault();
          highlightedIndex = Math.max(highlightedIndex - 1, -1);
          scrollToHighlighted();
          break;

        case "Enter":
          // always prevent form submission when dropdown is showing
          e.preventDefault();
          if (highlightedIndex >= 0) {
            e.stopImmediatePropagation();
            selectHighlightedItem(filtered[highlightedIndex]);
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          isFocused = false;
          inputValue = "";
          highlightedIndex = -1;
          break;
      }
    };

    inputElement.addEventListener("keydown", handleKeyDown, true);
    return () => inputElement.removeEventListener("keydown", handleKeyDown, true);
  });

  // close dropdown on scroll (but not when scrolling inside the dropdown)
  $effect(() => {
    if (!suggestionState().show) return;

    const handleScroll = (e: Event) => {
      // ignore scroll events from within the dropdown
      if (dropdownScrollContainer?.contains(e.target as Node)) return;

      isFocused = false;
      highlightedIndex = -1;
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  });

  // close dropdown on click outside
  $effect(() => {
    if (!suggestionState().show) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInsideContainer = containerElement?.contains(target);
      const isInsideDropdown = target.closest?.("[data-tags-input-dropdown]");

      if (!isInsideContainer && !isInsideDropdown) {
        isFocused = false;
        highlightedIndex = -1;
      }
    };

    // use capture to catch events before they're stopped by other handlers
    document.addEventListener("pointerdown", handleClickOutside, true);
    return () => document.removeEventListener("pointerdown", handleClickOutside, true);
  });

  function selectHighlightedItem(selectedSuggestion: string) {
    if (selectedSuggestion === DESELECT_ALL_OPTION) {
      deselectAllTags();
    } else {
      createTagDirectly(selectedSuggestion);
    }
    isFocused = false;
    inputValue = "";
    highlightedIndex = -1;
  }

  function scrollToHighlighted() {
    if (!dropdownScrollContainer || highlightedIndex < 0) return;

    const buttons = dropdownScrollContainer.querySelectorAll("button");
    const targetButton = buttons[highlightedIndex];
    if (targetButton) {
      targetButton.scrollIntoView({ block: "nearest" });
    }
  }

  const createTag = (value: string) => (addTag ? addTag(value) : value);

  const formatTag = (result: any) => {
    if (typeof result === "string" && result.trim()) return { id: result, value: result };
    if (result?.value?.trim()) return { id: result.id || result.value, value: result.value };
    return null;
  };

  // tag creation
  async function createTagDirectly(suggestion: string) {
    if (suggestion === ADD_ALL_OPTION) return addAllSuggestions();

    try {
      const result = await createTag(suggestion);
      const finalTag = formatTag(result);
      if (!finalTag) return;

      const isDuplicate =
        unique &&
        $tagsState.some((tag) => tag?.value?.toLowerCase() === finalTag.value.toLowerCase());

      if (!isDuplicate && $tagsState.length < maxTags) {
        const currentTags = $tagsState;
        tags.set([...currentTags, finalTag]);
      }
    } catch {}
  }

  // bulk add
  async function addAllSuggestions() {
    const existing = new Set(
      $tagsState.filter((tag) => tag?.value).map((tag) => tag.value.toLowerCase()),
    );

    const available = suggestions
      .filter((s) => {
        const lower = s.toLowerCase();
        const hasInput = inputValue && inputValue.length >= minQueryLength;
        const matchesInput = !hasInput || lower.includes(inputValue.toLowerCase());

        return matchesInput && !existing.has(lower);
      })
      .slice(0, maxTags - $tagsState.length);

    if (!available.length) return;

    try {
      const newTags = (
        await Promise.all(
          available.map(async (s) => {
            try {
              return formatTag(await createTag(s));
            } catch {
              return { id: s, value: s };
            }
          }),
        )
      ).filter((tag): tag is Tag => tag?.value);

      if (newTags.length) {
        const currentTags = $tagsState;
        tags.set([...currentTags, ...newTags]);
      }
    } catch {
      const currentTags = $tagsState;
      const fallbackTags = available.map((s) => ({ id: s, value: s }));
      tags.set([...currentTags, ...fallbackTags]);
    }
  }

  function deselectAllTags() {
    tags.set([]);
  }

  function truncateMiddle(str: string, maxLength = 42) {
    if (str.length <= maxLength) return str;

    const halfLength = Math.floor((maxLength - 3) / 2);
    const start = str.substring(0, halfLength);
    const end = str.substring(str.length - halfLength);

    return `${start}...${end}`;
  }
</script>

<div class="relative" bind:this={containerElement}>
  <div
    {...$root} use:$root.action
    class:disabled
    class="root relative flex min-h-[35px] min-w-[280px] items-center gap-2 rounded-lg bg-mono-000 align-top shadow-border-inset {wrap
      ? 'flex-wrap overflow-hidden'
      : 'flex-nowrap overflow-x-auto'} {($tagsState?.length || 0) === 0
      ? 'px-3'
      : 'px-1.5'} {className}"
    onclick={(e) => {
      e.stopPropagation();
      e.preventDefault();
    }}
  >
    <div class="flex w-full items-center gap-2 py-1.5 {wrap ? 'flex-wrap' : 'flex-nowrap'}">
      {#each $tagsState as t}
        {@const __MELTUI_BUILDER_2__ = $edit(t)}
        {@const __MELTUI_BUILDER_1__ = $deleteTrigger(t)}
        {@const __MELTUI_BUILDER_0__ = $tag(t)}
        {#if t?.value && t.id}
          {@const active = (t as TagsInputTag).active !== false}
          <div
            {...__MELTUI_BUILDER_0__} use:__MELTUI_BUILDER_0__.action
            class="flex h-[23px] items-center rounded-md border border-border bg-mono-200 pl-1.5 text-sm whitespace-nowrap text-foreground-primary data-[selected]:bg-mono-400"
            class:opacity-50={!active}
            in:scale={{ duration: 100, start: 0.95 }}
            out:scale={{ duration: 75, start: 1, opacity: 0 }}
            title={t.value}
          >
            <span class:line-through={!active}>{truncateMiddle(t.value)}</span>
            <div class="h-full">
              <button
                {...__MELTUI_BUILDER_1__} use:__MELTUI_BUILDER_1__.action
                type="button"
                class="flex h-full items-center truncate px-1 text-mono-700 transition-colors ease-out"
              >
                <span
                  class="hover:bg-magnum-300 flex h-full items-center justify-center rounded-r-md hover:text-mono-900"
                >
                  <Cross size={14} />
                </span>
              </button>
            </div>
          </div>
          <div
            {...__MELTUI_BUILDER_2__} use:__MELTUI_BUILDER_2__.action
            class="flex h-[23px] items-center overflow-hidden rounded-md px-1.5 pr-1 pl-1.5 text-sm [word-break:break-word] data-[invalid-edit]:focus:!ring-mono-500"
          ></div>
        {/if}
      {/each}
      <input
        bind:this={inputElement}
        bind:value={inputValue}
        {...$input} use:$input.action
        type="text"
        placeholder={($tagsState?.length ?? 0) >= maxTags ? "" : placeholder}
        class="flex-1 bg-transparent text-sm placeholder:text-placeholder focus:outline-hidden data-[invalid]:text-sec-800"
        onfocus={() => (isFocused = true)}
        onblur={() => (isFocused = false)}
        {disabled}
      />
    </div>
  </div>
</div>

<!-- portal dropdown -->
{#if suggestionState().show && typeof document !== "undefined" && typeof window !== "undefined"}
  {@const hasDeselectAll = suggestionState().filtered[0] === DESELECT_ALL_OPTION}
  {@const hasAddAll = suggestionState().filtered[hasDeselectAll ? 1 : 0] === ADD_ALL_OPTION}
  <div
    data-tags-input-dropdown
    role="listbox"
    tabindex="-1"
    class="menu fixed z-50 mt-1 flex max-h-[304px] flex-col overflow-hidden rounded-2xl text-sm text-mono-000 shadow-popover outline-hidden backdrop-blur-xs {dropdownClass}"
    style="top: {dropdownPosition.top}px; left: {dropdownPosition.left}px; width: {dropdownPosition.width}px;"
    transition:scale={{ duration: 100, start: 0.95 }}
    onmouseleave={() => {
      highlightedIndex = -1;
    }}
  >
    {#if hasDeselectAll}
      <div class="border-b border-b-mono-700">
        <button
          type="button"
          tabindex={highlightedIndex === 0 ? 0 : -1}
          class:highlighted={highlightedIndex === 0}
          class="dropdown-item block w-full cursor-pointer px-3 py-2.5 text-left text-sm focus:outline-hidden"
          onpointerdown={() => {
            deselectAllTags();
            isFocused = false;
            inputValue = "";
          }}
          onmouseenter={() => {
            highlightedIndex = 0;
          }}
        >
          <div class="flex items-center gap-1.5">
            <CrossCircle size={14} />
            <span>Deselect All ({$tagsState.length} items)</span>
          </div>
        </button>
      </div>
    {/if}
    {#if hasAddAll}
      <div class="border-b border-b-mono-700">
        <button
          type="button"
          tabindex={highlightedIndex === (hasDeselectAll ? 1 : 0) ? 0 : -1}
          class:highlighted={highlightedIndex === (hasDeselectAll ? 1 : 0)}
          class="dropdown-item block w-full cursor-pointer px-3 py-2.5 text-left text-sm focus:outline-hidden"
          onpointerdown={() => {
            createTagDirectly(ADD_ALL_OPTION);
            isFocused = false;
            inputValue = "";
          }}
          onmouseenter={() => {
            highlightedIndex = hasDeselectAll ? 1 : 0;
          }}
        >
          <div class="flex items-center gap-1.5">
            <PlusCircle size={14} />
            <span>Add All ({suggestionState().available} items)</span>
          </div>
        </button>
      </div>
    {/if}

    <!-- Scrollable suggestions -->
    {#if suggestionState().filtered.some((s) => s !== ADD_ALL_OPTION && s !== DESELECT_ALL_OPTION)}
      <div class="min-h-0 flex-1 overflow-auto p-1" bind:this={dropdownScrollContainer}>
        {#each suggestionState().filtered as suggestion, index}
          {#if suggestion !== ADD_ALL_OPTION && suggestion !== DESELECT_ALL_OPTION}
            <button
              type="button"
              tabindex={index === highlightedIndex ? 0 : -1}
              class:highlighted={index === highlightedIndex}
              class="dropdown-item block w-full cursor-pointer rounded-[11px] px-3 py-2 text-left text-sm focus:outline-hidden"
              onpointerdown={() => {
                createTagDirectly(suggestion);
                isFocused = false;
                inputValue = "";
              }}
              onmouseenter={() => {
                highlightedIndex = index;
              }}
            >
              {suggestion}
            </button>
          {/if}
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style>
  @reference "#tailwind.css";
  .menu {
    background: color-mix(in srgb, var(--color-mono-800) 80%, transparent);
    box-shadow:
      0px 1px 2px 0px rgba(0, 0, 0, 0.04),
      0px 16px 24px -12px rgba(0, 0, 0, 0.08);
  }
  .dropdown-item:hover {
    background-color: var(--color-mono-800) !important;
    box-shadow: none !important;
  }
  .highlighted {
    background-color: var(--color-mono-800) !important;
    box-shadow: none !important;
  }
  .border-b-mono-700 {
    border-bottom-color: var(--color-alpha-050) !important;
  }
  .root.disabled {
    background: var(--color-mono-200);
    cursor: not-allowed;

    input {
      cursor: not-allowed;
    }
  }
</style>
